import CmsBanner from "../../components/Banner/CmsBanner";
import ProfileImg from "../../assets/images/game-board/ahmed-farouqi-profile.png";
import { useQuery } from "@tanstack/react-query";
import user, { getCharacterListAPI, getLeaderboardListAPI, getMyRankAPI } from "../../api/user";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { getDefaultPortraitByGender, resolveCharacterUrlFromList } from "../../utils/charcter";

type LocalizedName = { en: string; ar: string; _id?: string };

interface LeaderboardUser {
  rank: number;
  id: string;
  full_name: string;
  email: string | null;
  profile_image: string | null;
  IQ?: number | null;
  total_points: number;
  flag: string | null;
  address?: string | { en: string; ar: string; _id?: string } | null;
  gender?: string | null;
  display_category_name?: LocalizedName | null;
  display_achievement_name?: LocalizedName | null;
  milestone?: {
    id: string;
    name: string | { en: string; ar: string };
    point_range_start: number;
    point_range_end: number;
  } | null;
}

const LeaderBoard = () => {
  // Check if user is logged in
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const isLoggedIn = token && token !== "undefined" && token !== "null" && token.trim() !== "";

  // State for user info modal
  const [selectedUser, setSelectedUser] = useState<LeaderboardUser | null>(null);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (selectedUser) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedUser]);

  const { data, isLoading } = useQuery({
    queryKey: ["leaderboardList", currentLanguage],
    queryFn: getLeaderboardListAPI,
  });

  const { data: myRankData, isLoading: myRankLoading } = useQuery({
    queryKey: ["myRank", currentLanguage],
    queryFn: getMyRankAPI,
    enabled: !!isLoggedIn, // Only fetch if user is logged in
  });

  const leaderboard: LeaderboardUser[] = data?.data?.leaderboard || [];
  const myRankDetails = myRankData?.data;
  // Check if user's rank is already in the leaderboard list
  const myUserId = myRankDetails?.user?.id;
  const isMyRankInList = leaderboard.some((user) => user.id === myUserId);

  // Get top 3 for podium (rank 2 on left, rank 1 in center, rank 3 on right)
  const rank1 = leaderboard.find((user) => user.rank === 1);
  const rank2 = leaderboard.find((user) => user.rank === 2);
  const rank3 = leaderboard.find((user) => user.rank === 3);

  // Get remaining users for the list (rank 4 and above)
  const remainingUsers = leaderboard.filter((user) => user.rank > 3);

  // Helper to get localized name from { en, ar } object
  const getLocalizedName = (obj: LocalizedName | null | undefined): string => {
    if (!obj) return "";
    return obj[currentLanguage as keyof LocalizedName] || obj.en;
  };

  // Display display_category_name + display_achievement_name (replaces milestone):
  // - Only badge → "Legend"
  // - Only category → "Sports"
  // - Both → "Sports Bronze"
  // - Neither → ""
  const getCategoryBadgeDisplay = (user: LeaderboardUser | null | undefined): string => {
    if (!user) return "";
    const categoryName = getLocalizedName(user.display_category_name);
    const badgeName = getLocalizedName(user.display_achievement_name);
    if (!badgeName && !categoryName) return "";
    // if (!badgeName) return categoryName;
    if (!categoryName) return badgeName;
    return `${categoryName} ${badgeName}`;
  };

  // Helper function to get address in current language
  const getAddress = (address: string | { en: string; ar: string; _id?: string } | null | undefined) => {
    if (!address) return "";
    if (typeof address === 'string') return address;
    return address[currentLanguage as keyof typeof address] || address.en;
  };

  const displayTotalPoints = (points: number | null | undefined) =>
    Math.round(Number(points ?? 0));

  const { data: portraitCharacterListData } = useQuery({
    queryKey: ["characterList", "portrait"],
    queryFn: () => getCharacterListAPI({ type: "portrait" }),
    staleTime: 1000 * 60 * 30,
  });
  const portraitCharacters = (portraitCharacterListData as any)?.data?.characters || [];

  const getProfileImageUrl = (user: LeaderboardUser | null | undefined) => {
    return (
      resolveCharacterUrlFromList(user?.profile_image, portraitCharacters) ||
      getDefaultPortraitByGender(user?.gender, portraitCharacters) ||
      null
    );
  };

  // Helper function to get profile image or default
  const getProfileImage = (user: LeaderboardUser) => {
    const profileImageUrl = getProfileImageUrl(user);
    return (<>
      {profileImageUrl
        ? (
          <img
            src={profileImageUrl}
            alt={user.full_name}
            className="object-cover"
          />
        ) : (
          <span className=" text-base font-bold text-white 2xl:h-[2.6vw] 2xl:w-[2.6vw] 2xl:rounded-[0.26vw] 2xl:border-[0.104vw] 2xl:text-[0.938vw]">
            {user.full_name?.charAt(0)?.toUpperCase() || "?"}
          </span>
        )}
    </>
    )
  };

  // Helper function to check if a user is the current logged-in user
  const isCurrentUser = (userId: string) => {
    return isLoggedIn && myUserId === userId;
  };

  return (
    <>
      <div className="wrapper">
        <CmsBanner
          title={t("leaderboard.title")}
          description={t("leaderboard.description")}
          classNameShape="hidden"

        />
        <section className="bg-primary-ivory 2xl:-mt-[2vw] md:-mt-5 -mt-5  2xl:py-[4vw] lg:py-15 py-10" >
          <div className="w-full 2xl:max-w-[71.667vw] max-w-[86rem] mx-auto 2xl:px-[0.833vw] xl:px-10 px-4">
            <div className="relative z-20">
              {/* Podium Section - Top 3 */}
              <div className="grid xs:grid-cols-3 grid-cols-1 2xl:gap-x-[2.09vw] xl:gap-x-10 2xl:gap-y-[2.09vw] md:gap-y-10 gap-y-5 gap-x-3.5">
                {/* Rank 2 - Left */}
                {rank2 && (
                  <div
                    onClick={() => setSelectedUser(rank2)}
                    className={`flex flex-col 2xl:mt-[3.38vw] xl:mt-16 xs:mt-10 cursor-pointer hover:scale-[1.02] transition-transform duration-200 ${isCurrentUser(rank2.id) ? "ring-4 ring-[#C3DC78] ring-offset-2 rounded-xl" : ""}`}
                  >
                    <div className="flex flex-col relative items-center -mb-6">
                      <div className="2xl:max-w-[6.2vw] md:max-w-[120px] max-w-[90px] 2xl:min-w-[6.2vw] md:min-w-[120px] min-w-[90px] aspect-square 2xl:p-[0.20vw] p-1 rounded-full text-center 2xl:max-w-[6.87vw] bg-[linear-gradient(180deg,#F2BF7D_0%,#F2BF7D_100%)]">
                        <span className="block bg-primary-ivory h-full rounded-full overflow-hidden  px-5 pb-3 pt-2 ">
                          {getProfileImage(rank2)}
                          {/* <img
                            src={getProfileImage(rank2)}
                            alt={rank2.full_name}
                            className="w-full h-full object-cover"
                          /> */}
                        </span>
                      </div>
                      <div className="2xl:perspective-[13.021vw] perspective-[250px] [perspective-origin:50%_100%] 2xl:p-[0.10vw] p-0.5 rounded-full text-center w-fit absolute bottom-0 z-10 start-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2">
                        <span className="block bg-primary-yellow 2xl:text-[0.84vw]/tight xl:text-base/tight text-xs/tight 2xl:p-[0.52vw] md:p-2.5 p-1 text-black-300 text-center whitespace-nowrap rotate-x-15
    2xl:border-[0.104vw] border-[1.5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] 2xl:rounded-[0.26vw] rounded-[5px] font-bold">
                          #{rank2.rank}
                          {`${getCategoryBadgeDisplay(rank2) ? ` | ${getCategoryBadgeDisplay(rank2)}` : ""}`}
                        </span>
                      </div>
                    </div>
                    <div className="bg-[linear-gradient(360deg,#DFB57E_-61.97%,#F0D2AA_56.12%)] 2xl:min-h-[1.71vw] min-h-8 [clip-path:polygon(20.824%_0%,0%_97.955%,99.914%_97.955%,82.694%_0%,20.824%_0%)]"></div>
                    <div className="bg-[linear-gradient(180deg,#DFB57E_0%,#F0D2AA_45.45%,#f0d2aa00_100%)] 2xl:p-[1.25vw] xl:p-6 p-4 2xl:-mt-[0.052vw] -mt-[1px]">
                      <div className="flex items-center justify-center gap-2 2xl:mb-[0.20vw] mb-1">
                        <span className="2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 block font-garamond font-semibold text-center capitalize">
                          {rank2.full_name}
                        </span>
                        {rank2.flag && (
                          <img
                            src={rank2.flag}
                            alt="country flag"
                            className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm"
                          />
                        )}
                        {isCurrentUser(rank2.id) && (
                          <span className="bg-[#91A459] text-white text-xs font-medium px-2 py-0.5 rounded-full">
                            {t("leaderboard.you")}
                          </span>
                        )}
                      </div>
                      <div className="2xl:w-[12vw] w-[200px] mx-auto skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center justify-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                        <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 text-white font-bold text-center font-garamond 2xl:text-[1.1vw]/tight xl:text-2xl/tight md:text-lg/tight text-xs/tight px-2">
                          {`IQ:${rank2?.IQ ?? 0} | ${displayTotalPoints(rank2.total_points)} ${t("leaderboard.points")}`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rank 1 - Center (Winner) */}
                {rank1 && (
                  <div
                    onClick={() => setSelectedUser(rank1)}
                    className={`flex flex-col xs:order-none -order-1 cursor-pointer hover:scale-[1.02] transition-transform duration-200 ${isCurrentUser(rank1.id) ? "ring-4 ring-primary-orange ring-offset-2 rounded-xl" : ""}`}
                  >
                    <div className="flex flex-col relative items-center -mb-6">
                      <div className="2xl:max-w-[6.2vw] md:max-w-[120px] max-w-[90px] 2xl:min-w-[6.2vw] md:min-w-[120px] min-w-[90px] aspect-square 2xl:p-[0.20vw] p-1 rounded-full text-center 2xl:max-w-[6.87vw] bg-primary-orange">
                        <span className="bg-linear-to-b from-primary-rose to-primary-orange h-full rounded-full flex justify-center items-center overflow-hidden px-5 pb-3 pt-2">
                          {getProfileImage(rank1)}
                          {/* <img
                            src={getProfileImage(rank1.profile_image)}
                            alt={rank1.full_name}
                            className="w-full h-full object-cover"
                          /> */}
                        </span>
                      </div>
                      <div className="2xl:perspective-[13.021vw] perspective-[250px] [perspective-origin:50%_100%] 2xl:p-[0.10vw] p-0.5 rounded-full text-center w-fit absolute bottom-0 z-10 start-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2">
                        <span className="block bg-primary-yellow 2xl:text-[0.84vw]/tight xl:text-base/tight text-xs/tight 2xl:p-[0.52vw] md:p-2.5 p-1 text-black-300 text-center whitespace-nowrap rotate-x-15
    2xl:border-[0.104vw] border-[1.5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] 2xl:rounded-[0.26vw] rounded-[5px] font-bold">
                          #{rank1.rank}
                          {`${getCategoryBadgeDisplay(rank1) ? ` | ${getCategoryBadgeDisplay(rank1)}` : ""}`}
                        </span>
                      </div>
                    </div>
                    <div className="bg-[linear-gradient(360deg,var(--color-primary-rose)_-61.97%,var(--color-primary-orange)_56.12%)] 2xl:min-h-[1.71vw] min-h-8 [clip-path:polygon(20.824%_0%,0%_97.955%,99.914%_97.955%,82.694%_0%,20.824%_0%)]"></div>
                    <div className="bg-[linear-gradient(180deg,var(--color-primary-orange)_0%,color-mix(in_srgb,var(--color-primary-light-orange)_75%,transparent)_50%,transparent_100%)] 2xl:p-[1.25vw] xl:p-6 p-4 2xl:-mt-[0.052vw] -mt-[1px]">
                      <div className="flex items-center justify-center gap-2 2xl:mb-[0.20vw] mb-1">
                        <span className="2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-white block font-garamond font-semibold text-center capitalize">
                          {rank1.full_name}
                        </span>
                        {rank1.flag && (
                          <img
                            src={rank1.flag}
                            alt="country flag"
                            className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm"
                          />
                        )}
                        {isCurrentUser(rank1.id) && (
                          <span className="bg-primary-yellow text-black-300 text-xs font-bold px-2 py-0.5 rounded-full">
                            {t("leaderboard.you")}
                          </span>
                        )}
                      </div>
                      <div className="2xl:w-[12vw] w-[200px] mx-auto skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-yellow flex items-center justify-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                        <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 text-black-300 font-bold text-center font-garamond 2xl:text-[1.1vw]/tight xl:text-2xl/tight md:text-lg/tight text-xs/tight px-2">
                          {`IQ:${rank1?.IQ ?? 0} | ${displayTotalPoints(rank1.total_points)} ${t("leaderboard.points")}`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rank 3 - Right */}
                {rank3 && (
                  <div
                    onClick={() => setSelectedUser(rank3)}
                    className={`flex flex-col 2xl:mt-[3.38vw] xl:mt-16 xs:mt-10 cursor-pointer hover:scale-[1.02] transition-transform duration-200 ${isCurrentUser(rank3.id) ? "ring-4 ring-primary-yellow ring-offset-2 rounded-xl" : ""}`}
                  >
                    <div className="flex flex-col relative items-center -mb-6">
                      <div className="2xl:max-w-[6.2vw] md:max-w-[120px] max-w-[90px] 2xl:min-w-[6.2vw] md:min-w-[120px] min-w-[90px] aspect-square 2xl:p-[0.20vw] p-1 rounded-full text-center 2xl:max-w-[6.87vw] bg-primary-yellow">
                        <span className="block bg-linear-to-b from-primary-yellow-level to-primary-yellow h-full rounded-full overflow-hidden px-5 pb-3 pt-2">
                          {getProfileImage(rank3)}
                          {/* <img
                            src={getProfileImage(rank3.profile_image)}
                            alt={rank3.full_name}
                            className="w-full h-full object-cover"
                          /> */}
                        </span>
                      </div>
                      <div className="2xl:perspective-[13.021vw] perspective-[250px] [perspective-origin:50%_100%] 2xl:p-[0.10vw] p-0.5 rounded-full text-center w-fit absolute bottom-0 z-10 start-1/2 ltr:-translate-x-1/2 rtl:translate-x-1/2">
                        <span className="block bg-brown-primary 2xl:text-[0.84vw]/tight xl:text-base/tight text-xs/tight 2xl:p-[0.52vw] md:p-2.5 p-1 text-black-300 text-center whitespace-nowrap rotate-x-15
    2xl:border-[0.104vw] border-[1.5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] 2xl:rounded-[0.26vw] rounded-[5px] font-bold">
                          #{rank3.rank}
                          {`${getCategoryBadgeDisplay(rank3) ? ` | ${getCategoryBadgeDisplay(rank3)}` : ""}`}
                        </span>
                      </div>

                    </div>
                    <div className="bg-[linear-gradient(360deg,var(--color-primary-yellow-level)_-61.97%,var(--color-primary-yellow)_56.12%)] 2xl:min-h-[1.71vw] min-h-8 [clip-path:polygon(20.824%_0%,0%_97.955%,99.914%_97.955%,82.694%_0%,20.824%_0%)]"></div>
                    <div className="bg-[linear-gradient(180deg,var(--color-primary-yellow)_0%,var(--color-primary-yellow)_45%,transparent_100%)] 2xl:p-[1.25vw] xl:p-6 p-4 2xl:-mt-[0.052vw] -mt-[1px]">
                      <div className="flex items-center justify-center gap-2 2xl:mb-[0.20vw] mb-1">
                        <span className="2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 block font-garamond font-semibold text-center capitalize">
                          {rank3.full_name}
                        </span>
                        {rank3.flag && (
                          <img
                            src={rank3.flag}
                            alt="country flag"
                            className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm"
                          />
                        )}
                        {isCurrentUser(rank3.id) && (
                          <span className="bg-primary-orange text-white text-xs font-medium px-2 py-0.5 rounded-full">
                            {t("leaderboard.you")}
                          </span>
                        )}
                      </div>
                      <div className="2xl:w-[12vw] w-[200px] mx-auto skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center justify-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                        <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 text-black-300 font-bold text-center font-garamond 2xl:text-[1.1vw]/tight xl:text-2xl/tight md:text-lg/tight text-xs/tight px-2">
                          {`IQ:${rank3?.IQ ?? 0} | ${displayTotalPoints(rank3.total_points)} ${t("leaderboard.points")}`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {/* Remaining Users List (Rank 4+) */}
              {remainingUsers.length > 0 && (
                <ul className="2xl:mt-[1.042vw] xl:mt-5 mt-4">
                  {remainingUsers.map((user: any) => {
                    const isMe = isCurrentUser(user.id);
                    return (
                      <li
                        key={user.id}
                        onClick={() => setSelectedUser(user)}
                        className={`2xl:mt-[1.25vw] mt-6 2xl:rounded-[0.833vw] rounded-2xl
                        2xl:shadow-[0.313vw_0.313vw_0px_0px_#110706] shadow-[6px_6px_0px_0px_#110706] bg-primary-ivory lg:border-[0.13vw] border-2 border-black-300 overflow-hidden cursor-pointer hover:scale-[1.005] transition-transform duration-200 ${isMe
                            ? ""
                            : ""
                          }`}
                      >
                        <div
                          className={` 2xl:rounded-[1.45vw] lg:rounded-[28px] rounded-2xl ${isMe
                            ? ""
                            : ""
                            }`}
                        >
                          <div
                            className="flex flex-col gap-2.5 px-2 min-w-0
                            sm:grid sm:items-center sm:gap-x-3 sm:gap-y-0 sm:px-3
                            2xl:gap-x-[0.52vw] 2xl:px-[0.84vw]
                            sm:grid-cols-[minmax(4rem,5.5rem)_minmax(0,1fr)_minmax(10.5rem,12rem)_minmax(9.5rem,1fr)]"
                          >
                            <div className="flex items-center gap-2 min-w-0 sm:contents">
                              <div className="relative flex shrink-0 2xl:gap-[1.04vw] xl:gap-5 gap-3 items-center justify-center justify-self-center">
                                <svg className="2xl:h-[5.625vw] xl:h-[60px] md:h-13 h-full 2xl:w-[7.24vw] xl:w-[80px] w-17 rtl:rotate-180" width="139" height="108" viewBox="0 0 139 108" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M137.117 -0.731445L136.994 0.378906L124.494 113.379L124.396 114.269H-1V-0.731445H137.117Z" fill="#FFFBA1" stroke="#110706" />
                                </svg>

                                <span className="absolute inset-0 flex items-center justify-center 2xl:text-[1.875vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 block font-garamond font-bold 2xl:min-w-[1.5vw] min-w-6">
                                  <span className="relative z-20">#{user.rank}</span>
                                </span>

                              </div>
                              <div className="flex min-w-0 flex-1 items-center 2xl:gap-[1.04vw] xl:gap-5 md:gap-3 gap-2 2xl:p-[0.84vw] p-1.5 sm:min-w-0">
                                <div className="2xl:w-[3vw] xl:w-[40px] lg:w-10 w-10 2xl:min-w-[3vw] xl:min-w-[40px] lg:min-w-10 min-w-10 2xl:h-[3vw] xl:h-[40px] lg:h-10 h-10 aspect-square overflow-hidden
bg-[#97A4F2] flex justify-center items-center skew-x-[-10deg]
2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] shrink-0">

                                  {resolveCharacterUrlFromList(user?.profile_image, portraitCharacters) ?
                                    (< img
                                      src={resolveCharacterUrlFromList(user?.profile_image, portraitCharacters)}
                                      alt={user.full_name}
                                      className="w-full h-full object-center"
                                    />
                                    ) : (
                                      <span className="text-base font-bold text-white 2xl:h-[2.6vw] 2xl:w-[2.6vw] 2xl:rounded-[0.26vw] 2xl:border-[0.104vw] 2xl:text-[0.938vw]">
                                        {user.full_name?.charAt(0)?.toUpperCase() || "?"}
                                      </span>
                                    )}
                                </div>
                                <span className="min-w-0 flex-1 truncate 2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 font-garamond font-bold">
                                  {user.full_name}
                                </span>
                                {user.flag && (
                                  <img
                                    src={user.flag}
                                    alt="country flag"
                                    className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm shrink-0"
                                  />
                                )}
                                {isMe && (
                                  <span className="bg-white/20 text-black-300 text-xs font-bold px-2 py-0.5 rounded-full shrink-0">
                                    {t("leaderboard.you")}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-3 min-w-0 sm:contents">
                              <div className="min-w-0 flex-1 sm:min-w-0 sm:w-full sm:max-w-none flex justify-start sm:justify-center px-1">
                                <span className="2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-sm/tight sm:text-base/tight text-black-300 font-garamond font-bold bg-white/10 rounded-full px-2 py-0.5 truncate text-start sm:text-center w-full sm:max-w-full">
                                  {getCategoryBadgeDisplay(user)}
                                </span>
                              </div>
                              <div className="shrink-0 text-end tabular-nums 2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-sm/tight sm:text-base/tight text-black-300 font-garamond font-bold whitespace-nowrap sm:justify-self-end">
                                {`IQ:${user?.IQ ?? 0} | ${displayTotalPoints(user.total_points)} ${t("leaderboard.points")}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="flex justify-center items-center py-10">
                  <span className="text-primary-orange font-garamond font-semibold">
                    <Loader />
                  </span>
                </div>
              )}

              {/* Empty State */}
              {!isLoading && leaderboard.length === 0 && (
                <div className="flex justify-center items-center py-10">
                  <span className="text-black-300 font-garamond font-medium">{t("leaderboard.noDataAvailable")}</span>
                </div>
              )}

              {/* User's Own Rank Section - Only shown if logged in and rank is NOT in the list */}
              {isLoggedIn && myRankDetails && !isMyRankInList && (
                <div className="2xl:mt-[2.5vw] mt-8">
                  <h3 className="2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 font-garamond font-semibold">
                    {t("leaderboard.yourRanking")}
                  </h3>
                  {/* Render my rank with the exact same <li> UI used in the ranks list */}
                  <ul className="mt-0">
                    <li
                      onClick={() => setSelectedUser({
                        rank: myRankDetails.rank,
                        id: myRankDetails.user?.id,
                        full_name: myRankDetails.user?.full_name,
                        email: myRankDetails.user?.email,
                        profile_image: myRankDetails.user?.profile_image || null,
                        IQ: myRankDetails.user?.IQ ?? null,
                        total_points: myRankDetails.total_points,
                        flag: myRankDetails.user?.flag,
                        address: myRankDetails.user?.address || null,
                        gender: myRankDetails.user?.gender || null,
                        display_category_name: myRankDetails.user?.display_category_name || null,
                        display_achievement_name: myRankDetails.user?.display_achievement_name || null,
                        milestone: myRankDetails.milestone || null,
                      })}
                      className={`2xl:mt-[1.25vw] mt-6 2xl:rounded-[0.833vw] rounded-2xl
                        2xl:shadow-[0.313vw_0.313vw_0px_0px_#110706] shadow-[6px_6px_0px_0px_#110706] bg-primary-ivory lg:border-[0.13vw] border-2 border-black-300 overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-200`}
                    >
                      <div className={` 2xl:rounded-[1.45vw] lg:rounded-[28px] rounded-2xl`}>
                        <div
                          className="flex flex-col gap-2.5 p-2 min-w-0
                          sm:grid sm:items-center sm:gap-x-3 sm:gap-y-0 sm:p-3
                          2xl:gap-x-[0.52vw] 2xl:p-[0.84vw]
                          sm:grid-cols-[minmax(4rem,5.5rem)_minmax(0,1fr)_minmax(10.5rem,12rem)_minmax(9.5rem,1fr)]"
                        >
                          <div className="flex items-center gap-2 min-w-0 sm:contents">
                            <div className="relative flex shrink-0 2xl:gap-[1.04vw] xl:gap-5 gap-3 items-center justify-center justify-self-center">
                              <svg className="2xl:h-[5.625vw] xl:h-[60px] md:h-13 h-full 2xl:w-[7.24vw] xl:w-[80px] w-17 rtl:rotate-180" width="139" height="108" viewBox="0 0 139 108" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M137.117 -0.731445L136.994 0.378906L124.494 113.379L124.396 114.269H-1V-0.731445H137.117Z" fill="#FFFBA1" stroke="#110706" />
                              </svg>

                              <span className="absolute inset-0 flex items-center justify-center 2xl:text-[1.875vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 block font-garamond font-bold 2xl:min-w-[1.5vw] min-w-6">
                                <span className="relative z-20">#{myRankDetails.rank}</span>
                              </span>
                            </div>
                            <div className="flex min-w-0 flex-1 items-center 2xl:gap-[1.04vw] xl:gap-5 md:gap-3 gap-2 2xl:p-[0.84vw] p-1.5 sm:min-w-0">
                              <div className="2xl:w-[3vw] xl:w-[40px] lg:w-10 w-10 2xl:min-w-[3vw] xl:min-w-[40px] lg:min-w-10 min-w-10 2xl:h-[3vw] xl:h-[40px] lg:h-10 h-10 aspect-square overflow-hidden
bg-[#97A4F2] skew-x-[-10deg]
2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] flex justify-center items-center shrink-0">

                                {resolveCharacterUrlFromList(myRankDetails?.user?.profile_image, portraitCharacters)
                                  ? (
                                    <img
                                      src={resolveCharacterUrlFromList(myRankDetails?.user?.profile_image, portraitCharacters)}
                                      alt={myRankDetails?.user?.full_name}
                                      className="w-full h-full object-center"
                                    />
                                  ) : (
                                    <span className="text-base font-bold text-white 2xl:h-[2.6vw] 2xl:w-[2.6vw] 2xl:rounded-[0.26vw] 2xl:border-[0.104vw] 2xl:text-[0.938vw]">
                                      {myRankDetails?.user?.full_name?.charAt(0)?.toUpperCase() || "?"}
                                    </span>
                                  )}
                              </div>
                              <span className="min-w-0 flex-1 truncate 2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-base/tight text-black-300 font-garamond font-bold">
                                {myRankDetails.user?.full_name}
                              </span>
                              {myRankDetails.user?.flag && (
                                <img
                                  src={myRankDetails.user.flag}
                                  alt="country flag"
                                  className="2xl:w-[1.2vw] 2xl:h-[0.9vw] w-5 h-4 object-cover rounded-sm shrink-0"
                                />
                              )}
                              <span className="bg-white/20 text-black-300 text-xs font-bold px-2 py-0.5 rounded-full shrink-0">
                                {t("leaderboard.you")}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-3 min-w-0 sm:contents">
                            <div className="min-w-0 flex-1 sm:min-w-0 sm:w-full sm:max-w-none flex justify-start sm:justify-center px-1">
                              <span className="2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-sm/tight sm:text-base/tight text-black-300 font-garamond font-bold bg-white/10 rounded-full px-2 py-0.5 truncate text-start sm:text-center w-full sm:max-w-full">
                                {getCategoryBadgeDisplay(myRankDetails?.user || myRankDetails)}
                              </span>
                            </div>
                            <div className="shrink-0 text-end tabular-nums 2xl:text-[1.25vw]/snug xl:text-2xl/snug md:text-lg/tight text-sm/tight sm:text-base/tight text-black-300 font-garamond font-bold whitespace-nowrap sm:justify-self-end">
                              {`IQ:${myRankDetails?.user?.IQ ?? 90} | ${displayTotalPoints(myRankDetails.total_points)} ${t("leaderboard.points")}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              )}

              {/* Loading My Rank */}
              {isLoggedIn && myRankLoading && (
                <div className="flex justify-center items-center py-5 2xl:mt-[1.04vw] mt-4">
                  <span className="text-primary-orange font-garamond font-medium text-sm">
                    {t("leaderboard.loadingYourRank")}
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* User Info Modal */}
      {selectedUser &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6 px-6 overflow-hidden"
            onClick={() => setSelectedUser(null)}
          >
            <div
              className="relative w-full max-w-lg max-h-[85vh] flex flex-col animate-[fadeIn_0.2s_ease-out]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Container with theme styling */}
              <div className="bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[40px] md:rounded-4xl rounded-3xl 2xl:p-[2vw] xl:p-10 md:p-7 p-4 lg:border-[0.2vw] border-3 border-primary overflow-y-auto px-6">
                {/* Header */}
                <div className="relative 2xl:mb-[1vw] mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="2xl:w-[4vw] xl:w-16 w-14 2xl:h-[4vw] xl:h-16 h-14 aspect-square overflow-hidden bg-[#97A4F2] flex justify-center items-center skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                        {getProfileImageUrl(selectedUser) ? (
                          <img src={getProfileImageUrl(selectedUser) || ""} alt={selectedUser.full_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-base font-bold text-white 2xl:h-[2.6vw] 2xl:w-[2.6vw] 2xl:rounded-[0.26vw] 2xl:border-[0.104vw] 2xl:text-[0.938vw]">
                            {selectedUser.full_name?.charAt(0)?.toUpperCase() || "?"}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-garamond font-bold text-primary-orange 2xl:text-[1.875vw]/[1.9vw] xl:text-4xl/6 text-2xl/6">
                          {selectedUser.full_name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          {selectedUser.flag && (
                            <img
                              src={selectedUser.flag}
                              alt="country flag"
                              className="2xl:w-[1.5vw] 2xl:h-[1.1vw] w-6 h-5 object-cover rounded-sm"
                            />
                          )}
                          <p className="font-garamond text-black-300 2xl:text-[0.9375vw] text-base/tight">
                            #{selectedUser.rank}
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer hover:scale-110 transition-transform duration-300"
                    >
                      <svg className="w-full h-full" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                          fill="#333938"
                        />
                      </svg>
                    </button>
                  </div>
                  {/* Gradient divider */}
                  <div className="w-full 2xl:mt-[1.042vw] mt-5">
                    <svg className="w-full" width="540" height="1" viewBox="0 0 540 1" fill="none">
                      <line
                        y1="-0.5"
                        x2="540"
                        y2="-0.5"
                        transform="matrix(-1 0 0 1 540 1)"
                        stroke="url(#paint0_linear_user)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_user"
                          x1="0"
                          y1="0.5"
                          x2="540"
                          y2="0.5"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#0B3369" stopOpacity="0" />
                          <stop offset="0.5" stopColor="#7364FF" />
                          <stop offset="1" stopColor="#0B3369" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>

                {/* User Stats */}
                <div className="2xl:space-y-[0.8vw] space-y-3">
                  {/* Total Points */}
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory 2xl:rounded-[0.26vw] rounded-[5px] 2xl:p-[0.8vw] p-3 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <div className="skew-x-[10deg] flex items-center justify-between">
                      <span className="font-garamond text-black-300 2xl:text-[0.9375vw] text-base/tight">
                        {t("leaderboard.totalPoints")}
                      </span>
                      <span className="font-garamond font-bold text-primary-orange 2xl:text-[1.2vw] text-xl/tight">
                        {displayTotalPoints(selectedUser.total_points)}
                      </span>
                    </div>
                  </div>

                  {/* Email
                  {selectedUser.email && (
                    <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory 2xl:rounded-[0.26vw] rounded-[5px] 2xl:p-[0.8vw] p-3 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                      <div className="skew-x-[10deg] flex items-center justify-between gap-2">
                        <span className="font-garamond text-black-300 2xl:text-[0.9375vw] text-base/tight">
                          {t("leaderboard.email")}
                        </span>
                        <span className="font-garamond font-bold text-black-300 2xl:text-[0.9375vw] text-base/tight truncate">
                          {selectedUser.email}
                        </span>
                      </div>
                    </div>
                  )} */}

                  {/* Milestone */}
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory 2xl:rounded-[0.26vw] rounded-[5px] 2xl:p-[0.8vw] p-3 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <div className="skew-x-[10deg] flex items-center justify-between">
                      <span className="font-garamond text-black-300 2xl:text-[0.9375vw] text-base/tight">
                        {t("leaderboard.milestone")}
                      </span>
                      <span className="font-garamond font-bold text-primary-orange 2xl:text-[0.9375vw] text-base/tight">
                        {getCategoryBadgeDisplay(selectedUser)}
                        {/* {getCategoryBadgeDisplay(selectedUser) || t("leaderboard.noBadgeEarned")} */}
                      </span>
                    </div>
                  </div>

                  {/* Gender */}
                  {selectedUser.gender && (
                    <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory 2xl:rounded-[0.26vw] rounded-[5px] 2xl:p-[0.8vw] p-3 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                      <div className="skew-x-[10deg] flex items-center justify-between">
                        <span className="font-garamond text-black-300 2xl:text-[0.9375vw] text-base/tight">
                          {t("leaderboard.gender")}
                        </span>
                        <span className="font-garamond font-bold text-black-300 2xl:text-[0.9375vw] text-base/tight capitalize">
                          {selectedUser.gender}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Nationality
                  {getAddress(selectedUser.address) && (
                    <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-ivory 2xl:rounded-[0.26vw] rounded-[5px] 2xl:p-[0.8vw] p-3 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                      <div className="skew-x-[10deg] flex items-center justify-between gap-2">
                        <span className="font-garamond text-black-300 2xl:text-[0.9375vw] text-base/tight">
                          {t("leaderboard.nationality")}
                        </span>
                        <div className="flex items-center gap-2">
                          {selectedUser.flag && (
                            <img
                              src={selectedUser.flag}
                              alt="country flag"
                              className="2xl:w-[1.5vw] 2xl:h-[1.1vw] w-6 h-5 object-cover rounded-sm"
                            />
                          )}
                          <span className="font-garamond font-bold text-black-300 2xl:text-[0.9375vw] text-base/tight">
                            {getAddress(selectedUser.address)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default LeaderBoard;
